// cleanup_convert_to_glu_component_GENERICS_TRANSFERS
// cleanup_convert_to_typescript_file_GENERICS_TRANSFERS
// cleanup_remove_external_libraries_GENERICS_TRANSFERS

import React, { useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';
import { useHistory, useLocation } from 'react-router';

import Alert from '@glu/alerts-react';
import { FormContext } from '@glu/validation-react';
import { Input } from '@glu/form-components';

import CurrencyInput from 'components/CurrencyInput/CurrencyInput';
import { WorkflowContext } from 'components/workflow/Workflow';
import useLocale from 'components/useLocale/useLocale';
import {
  TRANSFER_TABS,
  ALERTVARIATIONS,
  WORKSPACES,
  PAYMENT_ACTIONS,
  RECURRINGTRANSFERS
} from 'constants.js';
import { lt } from 'utils/validations/gluValidations';
import {
  createTransferInstruction,
  modifyTransferInstruction,
  validateName,
  deleteRecurringTransfers
} from 'api/transfers/service';
import InfoDisplay from 'components/infoDisplay/InfoDisplay';

import ConfirmModal from './RecurringTransferConfirmModal';
import DeleteModal from './RecurringTransferDeleteModal';
import Accounts from './RecurringTransferAccounts';
import { ModifyContext, ReadOnlyContext } from './contexts/ModifyRecurringTransfer.context';
import RecurringTransferFooter from './RecurringTransferFooter';
import Scheduler from './RecurringTransferScheduler';
import { RecurringTransferCancelModal } from './RecurringTransferCancelModal';
import { useStyles } from './recurringTransfer.styles';
import { mapFormDataToApi } from './recurringTransfer.utils';

const { ERROR } = ALERTVARIATIONS;
const { RECURRING } = TRANSFER_TABS;
const { TRANSFER } = WORKSPACES;

const RecurringTransferFormComponent = ({ useCanceling: [canceling, setCanceling] }) => {
  const [alert, setAlert] = useState();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formValidationInProgress, setFormValidationInProgress] = useState(false);
  const readOnly = useContext(ReadOnlyContext);
  const modify = useContext(ModifyContext);
  const locale = useLocale();
  const history = useHistory();
  const { state: { currentInstruction = '' } = {} } = useLocation();
  const {
    data,
    data: { amount, creditAccount, debitAccount, instructionId, memo, name } = {},
    validate
  } = useContext(FormContext);

  const { setAllowNavigation } = useContext(WorkflowContext);
  const [disableName, setDisableName] = useState(!!name);
  const classes = useStyles();
  const {
    alert: alertClass,
    inputs,
    memo: memoField,
    readOnly: readOnlyClass,
    scheduler,
    wrapper
  } = classes;
  useEffect(() => {
    if (modify && disableName) {
      setDisableName(true);
    }
  }, [modify, disableName]);

  const getCurrentAction = () => {
    if (modify) return PAYMENT_ACTIONS.MODIFY;
    if (confirming) return PAYMENT_ACTIONS.CREATE;
    if (deleting) return PAYMENT_ACTIONS.DELETE;
    return null;
  };

  const validateOnChange = (name) => validate(name);

  const handleCreate = () => {
    setFormValidationInProgress(true);
    validate().then(({ isValid }) => {
      if (isValid) {
        setConfirming(true);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    const { data: { amount, creditAccount = [], debitAccount = [] } = {}, isValid } =
      await validate();

    const request = mapFormDataToApi(data);
    // For the modifyTransferInstruction and createTransferInstruction api responses
    // if the request is successful then data comes back as an object
    // if the request is unsuccessful data comes back as an array
    if (isValid) {
      const {
        data: responseData = [], // these will be defined if request fails
        data: { approvalRequirement, instruction = '' } = {}, // these will be defined if request succeeds
        success
      } = modify
        ? await modifyTransferInstruction(request)
        : await createTransferInstruction(request);

      setConfirming(false);
      setSubmitting(false);
      setAllowNavigation(true);

      if (!success) {
        const { code, userMessage } = responseData?.[0] || {};
        setAlert(locale[code] || userMessage || locale['common.error']);
        return;
      }

      const message = {
        success: [
          {
            type: RECURRINGTRANSFERS,
            tab: RECURRING,
            details: [
              {
                transfer: {
                  awaitingApproval: false,
                  amount,
                  debitAccountName: debitAccount?.[0]?.standardName || '',
                  debitAccountNumber: debitAccount?.[0]?.number || '',
                  creditAccountName: creditAccount?.[0]?.standardName || '',
                  creditAccountNumber: creditAccount?.[0]?.number || '',
                  instruction,
                  approvalRequirement
                }
              }
            ]
          }
        ]
      };

      history.push({
        pathname: TRANSFER,
        state: {
          message,
          activeTab: RECURRING,
          action: getCurrentAction()
        }
      });
    }
  };

  const returnToRecurringTab = () =>
    history.push({
      pathname: history?.location?.state?.previous || TRANSFER,
      state: {
        activeTab: RECURRING
      }
    });

  const onDelete = () =>
    deleteRecurringTransfers([instructionId]).then((res) => {
      const success = res?.[`${instructionId}`].success;
      if (success) {
        const message = {
          success: [
            {
              type: RECURRINGTRANSFERS,
              transfer: {
                awaitingApproval: false,
                amount,
                debitAccountName: debitAccount?.[0]?.standardName || '',
                debitAccountNumber: debitAccount?.[0]?.number || '',
                creditAccountName: creditAccount?.[0]?.standardName || '',
                creditAccountNumber: creditAccount?.[0]?.number || '',
                instruction: currentInstruction || ''
              }
            }
          ]
        };
        history.push({
          pathname: TRANSFER,
          state: {
            message,
            activeTab: RECURRING,
            action: getCurrentAction()
          }
        });
      }
    });

  const validateNameFunc = useCallback(
    async (name, _, data) => {
      if ((modify && data.name) || readOnly) {
        return true;
      }
      const results = await validateName(data[name]).toPromise();
      setFormValidationInProgress(false);
      return results.valid;
    },
    [modify, readOnly]
  );

  return (
    <form data-qa="recurringTransferForm" onSubmit={handleSubmit}>
      {canceling && (
        <RecurringTransferCancelModal
          action={returnToRecurringTab}
          onClose={() => setCanceling(false)}
          opened
        />
      )}
      {deleting && <DeleteModal action={onDelete} onCancel={() => setDeleting(false)} />}
      {confirming && (
        <ConfirmModal
          modify={modify}
          onCancel={() => setConfirming(false)}
          submitDisabled={submitting}
        />
      )}
      {alert && (
        <div className={alertClass}>
          <Alert
            dataQa="error-alert"
            dismissible
            message={alert}
            onHide={() => setAlert(null)}
            variant={ERROR}
          />
        </div>
      )}
      <div className={classnames(wrapper, readOnly && readOnlyClass)}>
        <fieldset className={inputs} disabled={confirming}>
          <Input
            disabled={readOnly || disableName}
            labelText={locale['common.name']}
            maxLengthIndicatorLimit={!readOnly && 40}
            name="name"
            validateOnBlur
            validators={[
              {
                fn: validateNameFunc,
                msg: locale['common.nameMustBeUnique']
              },
              lt({ maxLength: 40 }),
              { required: true, msg: locale['validation.required'] }
            ]}
          />
          <Accounts
            creditAccountId={creditAccount?.id}
            debitAccountId={debitAccount?.id}
            readOnly={readOnly}
          />
          <CurrencyInput
            className="form-group"
            dataQa="currency-input"
            disabled={readOnly}
            hidePrefixSymbol={readOnly}
            labelText={locale['common.amount']}
            maxLength={15}
            name="amount"
            onChange={validateOnChange}
            validators={[
              { required: true, msg: locale['validation.required'] },
              { maxLength: 14, msg: template(locale['common.maxlength'])({ maxLength: 14 }) },
              {
                fn: (name, _, data) => {
                  const a = Number((data[name] || 0).replace(/,/g, ''));
                  return a > 0;
                },
                msg: template(locale['common.greaterThan'])({ field: '0.00' })
              }
            ]}
          />
          {!readOnly ? (
            <Input
              labelText={locale['common.memo']}
              maxLengthIndicatorLimit={80}
              name="memo"
              onChange={validateOnChange}
              optional
              validators={[lt({ maxLength: 80 })]}
            />
          ) : (
            <InfoDisplay
              containerClass="form-group"
              label={locale['common.memo']}
              value={memo}
              valueClass={memoField}
            />
          )}
        </fieldset>
        <fieldset className={scheduler} disabled={confirming || readOnly}>
          <Scheduler disabled={confirming || readOnly} readOnly={readOnly} />
        </fieldset>
        <RecurringTransferFooter
          amount={amount}
          classes={classes}
          formValidationInProgress={formValidationInProgress}
          instructionId={instructionId}
          onCancel={readOnly ? returnToRecurringTab : () => setCanceling(true)}
          onCreate={handleCreate}
          onDelete={() => setDeleting(true)}
          readOnly={readOnly}
        />
      </div>
    </form>
  );
};

RecurringTransferFormComponent.propTypes = {
  useCanceling: PropTypes.arrayOf([PropTypes.bool, PropTypes.func]).isRequired
};

export default RecurringTransferFormComponent;
